export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type InstallPromptListener = (event: InstallPromptEvent | null) => void;

let initialized = false;
let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<InstallPromptListener>();

export function initializeInstallPromptCapture() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    notifyInstallPromptListeners();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notifyInstallPromptListeners();
  });
}

export function getInstallPrompt(): InstallPromptEvent | null {
  return deferredPrompt;
}

export function subscribeInstallPrompt(listener: InstallPromptListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const prompt = deferredPrompt;
  if (!prompt) return "unavailable";

  await prompt.prompt();
  const choice = await prompt.userChoice;
  if (choice.outcome === "accepted") {
    deferredPrompt = null;
    notifyInstallPromptListeners();
  }
  return choice.outcome;
}

function notifyInstallPromptListeners() {
  for (const listener of listeners) {
    listener(deferredPrompt);
  }
}
